/*******************************************************************************
    plik  : frontend/src/pages/UploadPage.tsx
    autor : Michal Kolodziejski (2:480/112.10)
    data  : 2025.01.06
    copyright  : PTODT <https://ptodt.org.pl>

    opis  : Upload page with drag-and-drop file upload

    licencja : MIT
*******************************************************************************/

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadFile, getQueueStats, formatFileSize } from '../services/api';
import type { QueueStats } from '../types';

/**
 * @brief Upload page component with drag-and-drop support
 * @return Upload page JSX element
 */
const UploadPage: React.FC = () => {
    const navigate = useNavigate();
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [queueStats, setQueueStats] = useState<QueueStats | null>(null);
    const [demoMode, setDemoMode] = useState<boolean>(() => {
        return localStorage.getItem('demoMode') === 'true';
    });

    /**
     * @brief Fetches current queue statistics
     */
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const stats = await getQueueStats();
                setQueueStats(stats);
            } catch {
                /** @brief Silently fail - stats are optional */
            }
        };

        fetchStats();
        const interval = setInterval(fetchStats, 10000);
        return () => clearInterval(interval);
    }, []);

    /**
     * @brief Validates that file is an MSA or HFE file
     * @param file File to validate
     * @return True if valid
     */
    const validateFile = useCallback((file: File): boolean => {
        const extension = file.name.toLowerCase().split('.').pop();
        const validExtensions = ['msa', 'hfe'];
        if (!extension || !validExtensions.includes(extension)) {
            setError('Nieprawidłowy format pliku. Akceptowane są pliki .msa i .hfe');
            return false;
        }

        if (file.size > 10 * 1024 * 1024) {
            setError('Plik jest zbyt duży. Maksymalny rozmiar to 10 MB.');
            return false;
        }

        return true;
    }, []);

    /**
     * @brief Handles file selection
     * @param file Selected file
     */
    const handleFileSelect = useCallback((file: File) => {
        setError(null);
        if (validateFile(file)) {
            setSelectedFile(file);
        }
    }, [validateFile]);

    /**
     * @brief Handles demo mode toggle
     */
    const handleDemoModeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setDemoMode(checked);
        localStorage.setItem('demoMode', String(checked));
    }, []);

    /**
     * @brief Handles file upload
     */
    const handleUpload = useCallback(async () => {
        if (!selectedFile) return;

        setIsUploading(true);
        setError(null);

        try {
            const response = await uploadFile(selectedFile, demoMode);
            navigate(`/progress/${response.jobId}`);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Błąd podczas przesyłania pliku');
            setIsUploading(false);
        }
    }, [selectedFile, navigate, demoMode]);

    /**
     * @brief Handles drag over event
     * @param e Drag event
     */
    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    /**
     * @brief Handles drag leave event
     * @param e Drag event
     */
    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    /**
     * @brief Handles file drop
     * @param e Drop event
     */
    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFileSelect(files[0]);
        }
    }, [handleFileSelect]);

    /**
     * @brief Handles file input change
     * @param e Input change event
     */
    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            handleFileSelect(files[0]);
        }
    }, [handleFileSelect]);

    return (
        <div className="upload-page">
            <section className="upload-section">
                <h1 className="upload-section__title">Konwerter MSA/HFE → ST</h1>

                <p className="upload-section__description">
                    Przekonwertuj obraz dyskietki Atari ST z formatu MSA (Magic Shadow Archiver)
                    lub HFE (HxC Floppy Emulator) do formatu ST (surowy obraz sektorowy).
                </p>

                {queueStats && (
                    <div className="queue-status" role="status" aria-live="polite">
                        <span className="queue-status__label">Status kolejki:</span>
                        <span className="queue-status__value">
                            {queueStats.activeJobs}/{queueStats.maxConcurrentJobs} aktywnych
                            {queueStats.pendingJobs > 0 && (
                                <>, {queueStats.pendingJobs} oczekujących</>
                            )}
                        </span>
                    </div>
                )}

                <div
                    className={`dropzone ${isDragging ? 'dropzone--dragging' : ''} ${selectedFile ? 'dropzone--has-file' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    role="button"
                    tabIndex={0}
                    aria-label="Przeciągnij i upuść plik MSA lub HFE lub kliknij, aby wybrać"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            document.getElementById('file-input')?.click();
                        }
                    }}
                >
                    <input
                        type="file"
                        id="file-input"
                        accept=".msa,.hfe"
                        onChange={handleInputChange}
                        className="dropzone__input"
                        aria-describedby="file-help"
                    />

                    <div className="dropzone__content">
                        {selectedFile ? (
                            <>
                                <div className="dropzone__icon dropzone__icon--file" aria-hidden="true">
                                    📁
                                </div>
                                <p className="dropzone__filename">{selectedFile.name}</p>
                                <p className="dropzone__filesize">{formatFileSize(selectedFile.size)}</p>
                            </>
                        ) : (
                            <>
                                <div className="dropzone__icon" aria-hidden="true">
                                    💾
                                </div>
                                <p className="dropzone__text">
                                    Przeciągnij plik MSA lub HFE tutaj
                                </p>
                                <p className="dropzone__subtext">
                                    lub kliknij, aby wybrać plik
                                </p>
                            </>
                        )}
                    </div>
                </div>

                <p id="file-help" className="upload-section__help">
                    Akceptowane formaty: .msa (Magic Shadow Archiver), .hfe (HxC Floppy Emulator v1/v2/v3)
                    <br />
                    Maksymalny rozmiar: 10 MB
                </p>

                {error && (
                    <div className="error-message" role="alert">
                        <span className="error-message__icon" aria-hidden="true">⚠️</span>
                        {error}
                    </div>
                )}

                {selectedFile && (
                    <>
                        <div className="demo-mode-toggle">
                            <label className="demo-mode-toggle__label">
                                <input
                                    type="checkbox"
                                    checked={demoMode}
                                    onChange={handleDemoModeChange}
                                    className="demo-mode-toggle__checkbox"
                                />
                                <span className="demo-mode-toggle__text">
                                    Tryb demo (wolniejsza konwersja)
                                </span>
                            </label>
                        </div>
                        <button
                            type="button"
                            className="upload-button"
                            onClick={handleUpload}
                            disabled={isUploading}
                            aria-busy={isUploading}
                        >
                            {isUploading ? (
                                <>
                                    <span className="upload-button__spinner" aria-hidden="true"></span>
                                    Przesyłanie...
                                </>
                            ) : (
                                'Rozpocznij konwersję'
                            )}
                        </button>
                    </>
                )}
            </section>

            <section className="info-section" aria-labelledby="info-heading">
                <h2 id="info-heading" className="info-section__title">O konwerterze</h2>

                <div className="info-grid">
                    <div className="info-card">
                        <h3 className="info-card__title">Format MSA</h3>
                        <p className="info-card__text">
                            Magic Shadow Archiver (MSA) to format kompresji obrazów dyskietek
                            Atari ST używający algorytmu RLE. Pliki MSA są mniejsze niż
                            surowe obrazy ST.
                        </p>
                    </div>

                    <div className="info-card">
                        <h3 className="info-card__title">Format HFE</h3>
                        <p className="info-card__text">
                            HxC Floppy Emulator (HFE) to format bitstream używany przez emulatory
                            stacji dyskietek. Obsługiwane są wersje v1, v2 i v3.
                        </p>
                        <p className="info-card__warning">
                            Do zastosowań konwencjonalnych, przemysłowych i medycznych
                            zaleca się użycie formatu HFE v1.
                        </p>
                    </div>

                    <div className="info-card">
                        <h3 className="info-card__title">Format ST</h3>
                        <p className="info-card__text">
                            Surowy obraz sektorowy (ST) zawiera dokładną kopię wszystkich
                            sektorów dyskietki. Jest kompatybilny z większością emulatorów
                            Atari ST.
                        </p>
                    </div>

                    <div className="info-card">
                        <h3 className="info-card__title">Kolejka zadań</h3>
                        <p className="info-card__text">
                            Serwer przetwarza maksymalnie 5 plików jednocześnie.
                            Dodatkowe zadania czekają w kolejce FIFO.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default UploadPage;
